import React, { useState, useEffect, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useWordsData } from '../../lib/useWordsData';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { useWordAudio } from '../../lib/useWordAudio';
import './Verbs.css';

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const TASKS_BY_AGE = {
  '4': 'verb_tasks_4',
  '5': 'verb_tasks_5',
  '6': 'verb_tasks_6',
  '7-9': null, '10-12': null, '13-15': null, '16+': null
};

const Verbs = ({ ageGroup, onBack, onComplete }) => {
  const { data: wordsData, loading: wordsLoading } = useWordsData();
  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('Verbs');
  const { play: playWordAudio } = useWordAudio();
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [results, setResults] = useState([]); // Теперь храним объекты с флагами!
  const [showResult, setShowResult] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);

  const hasCompletedRef = useRef(false);

  const taskArrayKey = TASKS_BY_AGE[ageGroup];
  const tasks = wordsData && taskArrayKey ? wordsData.verbTasksBySet[taskArrayKey] : null;
  const currentTask = tasks ? tasks[currentQuestionIndex] : null;
  const totalQuestions = tasks ? tasks.length : 0;

  const getImageSrc = () => `${S3_BASE_URL}/${currentTask.image}.png`;

  // --- НОРМАЛИЗАЦИЯ ТЕКСТА (ДЛЯ РУССКОГО ЯЗЫКА) ---
  const normalize = (str) => str.toLowerCase().replace(/ё/g, 'е').trim();

  // --- АНАЛИЗАТОР МАТРИЦЫ (ИЩЕТ ОШИБКИ) ---
  const analyzeAnswer = (heardTextRaw, correctAnswerRaw, matrix, correctPath) => {
    const heard = normalize(heardTextRaw);
    const correct = normalize(correctAnswerRaw);

    if (heard === correct) {
      return { correct: true, flag: null, said: heardTextRaw };
    }

    if (!matrix) {
      return { correct: false, flag: 'unknown_word', said: heardTextRaw };
    }

    // Ищем сказанное слово во всей матрице
    for (const tense in matrix) {
      for (const number in matrix[tense]) {
        const numberData = matrix[tense][number];
        
        // Если множественное число прошедшего времени (это просто строка "шли")
        if (typeof numberData === 'string') {
          if (normalize(numberData) === heard) {
            if (tense !== correctPath.tense) return { correct: false, flag: 'confuses_tenses', said: heardTextRaw };
            if (number !== correctPath.number) return { correct: false, flag: 'confuses_number', said: heardTextRaw };
            return { correct: false, flag: 'incorrect_form', said: heardTextRaw };
          }
        } 
        // Если объект (лица или род)
        else if (typeof numberData === 'object') {
          for (const key in numberData) {
            if (normalize(numberData[key]) === heard) {
              if (tense !== correctPath.tense) return { correct: false, flag: 'confuses_tenses', said: heardTextRaw };
              if (number !== correctPath.number) return { correct: false, flag: 'confuses_number', said: heardTextRaw };
              // Если время и число совпадают, но лицо/род нет
              return { correct: false, flag: 'confuses_person_gender', said: heardTextRaw };
            }
          }
        }
      }
    }

    // Если слово вообще не найдено в матрице
    return { correct: false, flag: 'unknown_word', said: heardTextRaw };
  };

  // Все возможные формы глагола из матрицы спряжения — используются как
  // Vosk-грамматика, чтобы распознаватель мог услышать любую из спутанных
  // форм (не только правильную), а analyzeAnswer потом определит тип ошибки.
  const flattenMatrix = (matrix) => {
    const forms = [];
    if (!matrix) return forms;
    for (const tense in matrix) {
      for (const number in matrix[tense]) {
        const numberData = matrix[tense][number];
        if (typeof numberData === 'string') {
          forms.push(numberData);
        } else if (typeof numberData === 'object') {
          for (const key in numberData) forms.push(numberData[key]);
        }
      }
    }
    return forms;
  };

  // --- ОЗВУЧКА ВОПРОСА + РАСПОЗНАВАНИЕ РЕЧИ ---
  // Запускаются одновременно: раньше распознаватель стартовал только после
  // utterance.onend и не успевал подключить микрофон вовремя.
  const speakQuestion = () => {
    if (!currentTask || isSpeaking) return;
    setIsSpeaking(true);
    setRecognizerReady(false);

    startListening();

    playWordAudio(currentTask.promptAudioUrl, () => setIsSpeaking(false));
  };

  // --- РАСПОЗНАВАНИЕ РЕЧИ ---
  const startListening = () => {
    const forms = Array.from(new Set([...flattenMatrix(currentTask.verbConjugation), currentTask.answer]));

    startSpeakRecognition(
      forms.map((form) => ({ key: form, text: form })),
      {
        onListening: () => setRecognizerReady(true),
        onResult: ({ said, timedOut }) => {
          setIsListening(false);
          const analysisResult = timedOut
            ? { correct: false, flag: 'silence', said: '' }
            : analyzeAnswer(
                said,
                currentTask.answer,
                currentTask.verbConjugation,
                { tense: currentTask.tense, number: currentTask.number, person: currentTask.person }
              );

          console.log('🧠 РЕЗУЛЬТАТ АНАЛИЗА:', analysisResult); // Логопед увидит это в консоли!
          handleAnswer(analysisResult);
        },
      }
    );
  };

  // isListening (🎧) reflects the recognizer actually being ready, not just
  // "the announcement finished".
  useEffect(() => {
    setIsListening(!isSpeaking && recognizerReady);
  }, [isSpeaking, recognizerReady]);

  const handleAnswer = (resultObject) => {
    setIsProcessing(true);
    setResults(prev => [...prev, resultObject]);
    stopSpeakRecognition();

    setTimeout(() => {
      if (currentQuestionIndex < totalQuestions - 1) {
        goToNext();
      } else {
        setShowResult(true);
      }
    }, 1500);
  };

  const goToNext = () => {
    setCurrentQuestionIndex(prev => prev + 1);
    setIsProcessing(false);
  };

  useEffect(() => {
    if (currentTask && !showResult && !isSpeaking && !isListening && !isProcessing) {
      const timer = setTimeout(speakQuestion, 600);
      return () => clearTimeout(timer);
    }
    // See Generalization.js: currentTask starts null until useWordsData
    // resolves, so it must be a dependency or the effect never re-fires
    // once the data actually arrives.
  }, [currentQuestionIndex, showResult, currentTask]);

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true;
          if (onComplete) onComplete(); else onBack();
        }
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

   // === ПЕРЕДАЧА РЕЗУЛЬТАТА В ОТЧЕТ ===
  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        // Используем results вместо answers
        const correctCount = results.filter(r => r.correct === true).length;
        const score = results.length > 0 ? Math.round((correctCount / results.length) * 100) : 0;
        
        if (onComplete) onComplete({ testId: 'Verbs', score: score });
        else onBack();
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = results.filter(r => r.correct).length;
    return (
      <ModuleTemplate progress={results.map(r => r.correct)} totalQuestions={totalQuestions} forceStop={true} onComplete={onComplete || onBack}>
        <div className="verbs-result-content">
          <h2 className="verbs-result-title">🎉 Упражнение завершено!</h2>
          <div className="verbs-result-score">
            <span className="verbs-score-number">{correctCount}</span>
            <span className="verbs-score-total"> / {totalQuestions}</span>
          </div>
          {/* ВРЕМЕННЫЙ БЛОК ДЛЯ ТЕСТИРОВАНИЯ - ПОКАЗАТЬ ФЛАГИ НА ЭКРАНЕ */}
          <div style={{ marginTop: '20px', textAlign: 'left', fontSize: '14px', color: '#666', maxHeight: '200px', overflowY: 'auto' }}>
            <h4 style={{marginBottom:'10px'}}>Диагностика (тест):</h4>
            {results.map((r, i) => (
              <div key={i} style={{marginBottom:'5px'}}>
                {r.correct ? '✅' : '❌'} {r.flag ? `[${r.flag}]` : ''} Сказал: "{r.said || 'тишина'}"
              </div>
            ))}
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (wordsLoading) return (
    <ModuleTemplate progress={results.map(r => r.correct)} totalQuestions={0} forceStop={true} onComplete={onComplete || onBack}>
      <div className="verbs-loading-text">Загрузка...</div>
    </ModuleTemplate>
  );

  if (!currentTask) return (
    <ModuleTemplate progress={results.map(r => r.correct)} totalQuestions={0} forceStop={true} onComplete={onComplete || onBack}>
      <div className="verbs-loading-text">Для этого возраста пока нет заданий</div>
    </ModuleTemplate>
  );

  return (
    <ModuleTemplate 
      progress={results.map(r => r.correct)} 
      totalQuestions={totalQuestions} 
      forceStop={true}             
      onComplete={onComplete}      
      extraContent="Продолжи фразу"
    >
      <div className="verbs-container">
        <div className="verbs-main-row">
          <div className="verbs-image-card">
            <img src={getImageSrc()} alt="" className="verbs-img" onError={(e)=>e.target.src='/placeholder.png'} />
          </div>
          <div className="verbs-indicators-col">
            <div className={`indicator listen ${isListening ? 'active' : ''}`}><span className="indicator-icon">🎧</span></div>
            <div className={`indicator speak ${isSpeaking ? 'active' : ''}`}><span className="indicator-icon">🗣️</span></div>
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default Verbs;